import { useState, useMemo, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Palmtree, CalendarDays, Calculator, CheckCircle2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { calculateTimeBlocks, calculateTotalWorkMinutes } from '../utils/timeCalculator';

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string | number | undefined;
  onChange: (value: string) => void;
}

function DebouncedInput({ value, onChange, ...props }: DebouncedInputProps) {
  const [localVal, setLocalVal] = useState(value?.toString() || '');
  const [prevVal, setPrevVal] = useState(value);
  const [isTyping, setIsTyping] = useState(false);

  if (value !== prevVal) {
    setPrevVal(value);
    setLocalVal(value?.toString() || '');
  }

  useEffect(() => {
    if (isTyping) {
      const handler = setTimeout(() => {
        onChange(localVal);
        setIsTyping(false);
      }, 800);
      return () => clearTimeout(handler);
    }
  }, [localVal, isTyping, onChange]);

  return (
    <input
      {...props}
      value={localVal}
      onChange={e => {
        setLocalVal(e.target.value);
        setIsTyping(true);
      }}
      onBlur={() => {
        if (isTyping) {
          onChange(localVal);
          setIsTyping(false);
        }
      }}
    />
  );
}

export function VacationTracker() {
  const { yearlyVacations, monthlyVacations, updateYearlyVacation, updateMonthlyVacation, entries, projects } = useData();
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

  const userEntries = useMemo(() => {
    return user?.id ? entries.filter(e => !e.userId || e.userId === user.id) : entries;
  }, [entries, user]);

  const months = useMemo(() => {
    const allMonths: string[] = [];
    for (let i = 1; i <= 12; i++) {
      allMonths.push(`${selectedYear}-${i.toString().padStart(2, '0')}`);
    }
    return allMonths;
  }, [selectedYear]);

  const currentYearlyVacation = yearlyVacations.find(v => v.year === selectedYear);
  const totalAvailable = currentYearlyVacation ? currentYearlyVacation.totalDays : 0;

  const handleYearlyChange = async (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num) && val !== '') return;
    await updateYearlyVacation(selectedYear, isNaN(num) ? 0 : num);
  };

  const handleMonthlyChange = async (month: string, val: string) => {
    const num = parseFloat(val);
    if (isNaN(num) && val !== '') return;
    await updateMonthlyVacation(month, isNaN(num) ? 0 : num);
  };

  const vacationActualsByMonth = useMemo(() => {
    const acc: Record<string, number> = {};
    const keywords = ['urlaub', 'ferien', 'vacation'];

    for (const entry of userEntries) {
      const month = entry.date.substring(0, 7);
      if (!acc[month]) acc[month] = 0;

      const blocks = calculateTimeBlocks(
        entry.timeArrived,
        entry.timeLeft,
        entry.breakDurationMinutes,
        entry.projects,
        projects
      );

      for (const b of blocks) {
        const proj = projects.find(p => p.id === b.projectId);
        const projName = proj?.name.toLowerCase() || '';
        const desc = b.description.toLowerCase();

        const isInternal = projName === 'internal' || projName.includes('intern');
        const isVacation = isInternal && keywords.some(kw => desc.includes(kw));

        if (isVacation) {
          const mins = typeof b.workMinutes === 'number' && b.workMinutes > 0
            ? b.workMinutes
            : calculateTotalWorkMinutes(b.timeArrived, b.timeLeft, b.breakDurationMinutes);
          acc[month] += Math.max(0, mins);
        }
      }
    }
    return acc;
  }, [userEntries, projects]);

  const rowData = months.map(month => {
    const mVacation = monthlyVacations.find(v => v.month === month);
    const actualMins = vacationActualsByMonth[month] || 0;
    return {
      month,
      planned: mVacation ? mVacation.plannedDays : 0,
      actual: (actualMins / 60) / 8.4
    };
  });

  const totalPlanned = rowData.reduce((acc, row) => acc + row.planned, 0);
  const totalActual = rowData.reduce((acc, row) => acc + row.actual, 0);
  const totalRemaining = totalAvailable - totalPlanned;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Vacation Days</h1>
          <p className="text-slate-500 mt-1">Plan your yearly allowance across the months.</p>
        </div>
        <div>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm bg-white font-medium text-slate-700 min-w-[120px]"
          >
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y.toString()}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
          <div className="bg-indigo-50 p-3 rounded-lg text-indigo-600">
            <Palmtree className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Total Allowance</p>
            <div className="mt-1 flex items-center gap-2">
              <DebouncedInput
                type="number"
                step="0.5"
                min="0"
                value={totalAvailable || ''}
                placeholder="0"
                onChange={handleYearlyChange}
                className="w-20 px-2 py-1 text-lg font-bold border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <span className="text-slate-500 font-medium">days</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
          <div className="bg-amber-50 p-3 rounded-lg text-amber-600">
            <CalendarDays className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Total Planned</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{totalPlanned.toFixed(1)}d</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
          <div className="bg-blue-50 p-3 rounded-lg text-blue-600">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Total Taken</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{totalActual.toFixed(1)}d</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex items-start gap-4">
          <div className={`p-3 rounded-lg ${totalRemaining < 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
            <Calculator className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Remaining</p>
            <p className={`text-2xl font-bold mt-1 ${totalRemaining < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
              {totalRemaining.toFixed(1)}d
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto relative">
          <table className="w-full text-left border-collapse min-w-max">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-xs font-semibold text-slate-700 uppercase tracking-wider sticky left-0 bg-slate-50 border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Metric</th>
                {rowData.map(row => (
                  <th key={row.month} className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tracking-wider w-20">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{row.month.substring(0, 4)}</span>
                      <span className="text-sm">{format(parseISO(row.month + '-01'), 'MMM')}</span>
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-bold text-slate-800 uppercase tracking-wider text-right bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">Grand Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-white border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Planned Days
                </td>
                {rowData.map(row => (
                  <td key={row.month} className="px-2 py-3 text-center">
                    <DebouncedInput
                      type="number"
                      step="0.5"
                      min="0"
                      value={row.planned || ''}
                      placeholder="0"
                      onChange={(val: string) => handleMonthlyChange(row.month, val)}
                      className="w-16 px-1 py-1 text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none text-sm mx-auto"
                    />
                  </td>
                ))}
                <td className="px-4 py-3 text-sm text-right font-bold text-slate-800 bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  {totalPlanned.toFixed(1)}
                </td>
              </tr>
              <tr className="hover:bg-slate-50/50 transition-colors bg-slate-50/30">
                <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-slate-50/90 border-r border-slate-200 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Vacation Taken (d)
                </td>
                {rowData.map(row => (
                  <td key={row.month} className="px-2 py-3 text-center text-sm font-medium text-slate-700">
                    {row.actual > 0 ? row.actual.toFixed(1) : '-'}
                  </td>
                ))}
                <td className="px-4 py-3 text-sm text-right font-bold text-slate-800 bg-indigo-50/50 border-l border-slate-200 sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  {totalActual.toFixed(1)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
